// Habit Stats chart preferences (range presets + default lens).
//
// Account-scoped through the `habit_stats` namespace when a
// PreferencesProvider is mounted, per-browser localStorage otherwise. Domain
// validity (which range/lens names exist) is enforced here rather than
// server-side, so new ranges and lenses ship without a backend release.
// The defensive load/validate/persist shape mirrors AnalyticsReferenceDateContext.
import { useCallback, useEffect, useMemo, useState } from 'react';

import { usePreferences } from '../contexts/PreferencesContext';

import {
  DEFAULT_HEALTH_LENS,
  HealthLens,
  LENS_ORDER,
  RANGE_ORDER,
  RangeOption,
} from '../pages/habitStatsTypes';

export const HABIT_STATS_RANGE_SETTINGS_KEY = 'kalpa-habit-stats-range-settings';
export const HABIT_STATS_LENS_SETTINGS_KEY = 'kalpa-habit-stats-health-lens-settings';

export type RangeEnabledMap = Record<RangeOption, boolean>;

// All presets on by default; Lifetime + Custom must always default on.
export const DEFAULT_RANGE_SETTINGS: RangeEnabledMap = RANGE_ORDER.reduce(
  (acc, key) => {
    acc[key] = true;
    return acc;
  },
  {} as RangeEnabledMap,
);

const hasAnyEnabled = (map: RangeEnabledMap): boolean =>
  RANGE_ORDER.some((key) => map[key]);

const loadRangeSettings = (
  stored?: Record<string, unknown> | null,
): RangeEnabledMap => {
  try {
    let parsed: Partial<Record<string, unknown>>;
    if (stored !== undefined) {
      if (!stored || Object.keys(stored).length === 0) {
        return { ...DEFAULT_RANGE_SETTINGS };
      }
      parsed = stored;
    } else {
      const raw = localStorage.getItem(HABIT_STATS_RANGE_SETTINGS_KEY);
      if (!raw) return { ...DEFAULT_RANGE_SETTINGS };
      parsed = JSON.parse(raw) as Partial<Record<string, unknown>>;
    }
    const merged: RangeEnabledMap = { ...DEFAULT_RANGE_SETTINGS };
    RANGE_ORDER.forEach((key) => {
      if (typeof parsed[key] === 'boolean') {
        merged[key] = parsed[key] as boolean;
      }
    });
    // Invariant: at least one range must remain enabled.
    if (!hasAnyEnabled(merged)) return { ...DEFAULT_RANGE_SETTINGS };
    return merged;
  } catch {
    return { ...DEFAULT_RANGE_SETTINGS };
  }
};

export function useHabitStatsRangeSettings() {
  const store = usePreferences();
  const [localRanges, setLocalRanges] = useState<RangeEnabledMap>(() =>
    loadRangeSettings(),
  );

  const enabledRanges = useMemo(() => {
    if (!store) return localRanges;
    const namespace = store.settings.habit_stats as Record<string, unknown>;
    return loadRangeSettings(
      (namespace?.enabled_ranges as Record<string, unknown>) ?? null,
    );
  }, [store, localRanges]);

  const writeRanges = useCallback(
    (next: RangeEnabledMap) => {
      setLocalRanges(next);
      if (store) {
        const namespace = (store.settings.habit_stats || {}) as Record<string, unknown>;
        store.setNamespace('habit_stats', { ...namespace, enabled_ranges: next });
      }
    },
    [store],
  );

  useEffect(() => {
    // Providerless fallback only — see the note in useNavPreferences.
    if (store) return;
    try {
      localStorage.setItem(
        HABIT_STATS_RANGE_SETTINGS_KEY,
        JSON.stringify(enabledRanges),
      );
    } catch {
      // Ignore storage failures; the in-memory setting still works this session.
    }
  }, [enabledRanges, store]);

  const setRangeEnabled = useCallback(
    (range: RangeOption, enabled: boolean) => {
      const next = { ...enabledRanges, [range]: enabled };
      // Refuse to disable the last enabled preset.
      if (!hasAnyEnabled(next)) return;
      writeRanges(next);
    },
    [enabledRanges, writeRanges],
  );

  const resetToDefault = useCallback(
    () => writeRanges({ ...DEFAULT_RANGE_SETTINGS }),
    [writeRanges],
  );

  return { enabledRanges, setRangeEnabled, resetToDefault };
}

const loadDefaultLens = (stored?: string | null): HealthLens => {
  try {
    let candidate: unknown;
    if (stored !== undefined) {
      candidate = stored;
    } else {
      const raw = localStorage.getItem(HABIT_STATS_LENS_SETTINGS_KEY);
      if (!raw) return DEFAULT_HEALTH_LENS;
      candidate = (JSON.parse(raw) as { defaultLens?: unknown }).defaultLens;
    }
    if (
      typeof candidate === 'string' &&
      (LENS_ORDER as string[]).includes(candidate)
    ) {
      return candidate as HealthLens;
    }
  } catch {
    // fall through to default
  }
  return DEFAULT_HEALTH_LENS;
};

export function useHabitStatsDefaultLens() {
  const store = usePreferences();
  const [localLens, setLocalLens] = useState<HealthLens>(() => loadDefaultLens());

  const defaultLens = useMemo(() => {
    if (!store) return localLens;
    const namespace = store.settings.habit_stats as Record<string, unknown>;
    return loadDefaultLens((namespace?.default_lens as string) ?? null);
  }, [store, localLens]);

  const setDefaultLens = useCallback(
    (lens: HealthLens) => {
      setLocalLens(lens);
      if (store) {
        const namespace = (store.settings.habit_stats || {}) as Record<string, unknown>;
        store.setNamespace('habit_stats', { ...namespace, default_lens: lens });
      }
    },
    [store],
  );

  useEffect(() => {
    // Providerless fallback only — see the note in useNavPreferences.
    if (store) return;
    try {
      localStorage.setItem(
        HABIT_STATS_LENS_SETTINGS_KEY,
        JSON.stringify({ defaultLens }),
      );
    } catch {
      // Ignore storage failures.
    }
  }, [defaultLens, store]);

  return { defaultLens, setDefaultLens };
}
